import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const Kerrisdale = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kerrisdale-vancouver:About Kerrisdale Paragraph">
          Kerrisdale, located in{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          , is a charming neighborhood known for its tree-lined streets and
          vibrant community atmosphere. Situated on Vancouver’s west side,
          Kerrisdale features a mix of older bungalows and newer homes,
          reflecting its rich history and ongoing development. The area is
          renowned for its shopping district along West 41st Avenue, offering a
          variety of boutiques, cafes, and restaurants that cater to diverse
          tastes. With its blend of residential tranquility and urban amenities,
          Kerrisdale provides a unique and appealing environment for residents
          and visitors alike.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,100 - $2,800',
          bed2: '$3,100 - $3,800',
          bed3: '$4,200 - $4,800'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.kerrisdale-vancouver:Apartment Hunting Kerrisdale Paragraph 1">
            Finding an apartment for rent in Kerrisdale, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Kerrisdale lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.kerrisdale-vancouver:Apartment Hunting Kerrisdale Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kerrisdale-vancouver:Apartments for rent in Kerrisdale Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Kerrisdale, Vancouver
              </LinkText>
              , are ideal for professionals, students, and retirees seeking a
              balance between city living and suburban comfort. These units
              range from cozy studios to spacious multi-bedroom apartments,
              often featuring modern amenities. Renting in Kerrisdale offers
              proximity to shopping districts, parks, and public transit, making
              it a convenient and attractive option.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kerrisdale-vancouver:Houses for rent in Kerrisdale Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Kerrisdale, Vancouver
              </LinkText>
              , cater to families and individuals desiring more space and
              privacy. These homes often boast multiple bedrooms, private
              gardens, and classic architectural designs. Renting a house in
              this neighborhood provides access to reputable schools, community
              centers, and a family-friendly environment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kerrisdale-vancouver:Townhouses for rent in Kerrisdale Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Kerrisdale, Vancouver
              </LinkText>{' '}
              , offer a middle ground between apartments and standalone houses.
              They are suitable for young families, professionals, or downsizers
              seeking modern living spaces with multiple levels. Advantages
              include contemporary designs, shared amenities, and a sense of
              community within the neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kerrisdale-vancouver:Basements for rent in Kerrisdale Paragraph">
              Basements for rent in Kerrisdale, Vancouver, provide affordable
              housing options for students, singles, or young couples. These
              units typically offer one or two bedrooms with basic amenities.
              Renting a basement suite in Kerrisdale allows residents to enjoy
              the neighborhood’s benefits without a high price tag.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kerrisdale-vancouver:Condos for rent Kerrisdale Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Kerrisdale, Vancouver
              </LinkText>
              , are ideal for individuals or small families seeking modern
              living spaces with added amenities like gyms or concierge
              services. These units vary in size and style, offering
              contemporary designs and the convenience of low-maintenance
              living.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kerrisdale-vancouver:Rooms for rent in Kerrisdale Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Kerrisdale, Vancouver
              </LinkText>{' '}
              , are perfect for students or individuals seeking budget-friendly
              accommodations. These arrangements often involve shared common
              areas, providing an affordable way to live in a desirable
              neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.kerrisdale-vancouver:Short term rentals in Kerrisdale Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in Kerrisdale, Vancouver
              </LinkText>
              , cater to tourists, business travelers, or individuals in
              transition. These furnished units range from private rooms to
              entire homes, offering flexibility and the comforts of home during
              a temporary stay.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kerrisdale-vancouver:Exploring Kerrisdale Paragraph">
          Kerrisdale, situated in Vancouver, British Columbia, is a neighborhood
          that seamlessly blends historic charm with modern amenities. Known for
          its picturesque streets lined with mature trees, Kerrisdale exudes a
          welcoming and affluent atmosphere. The heart of the neighborhood is
          Kerrisdale Village, a bustling shopping district along West 41st
          Avenue, featuring an array of boutiques, cafes, and specialty stores.
          Landmarks such as the Kerrisdale{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/kerrisdale-cyclone-taylor-arena.aspx">
            Cyclone Taylor Arena
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://kerrisdalecc.com/">
            Kerrisdale Community Centre
          </LinkText>{' '}
          serve as hubs for recreational activities and community events. The
          area appeals to families, professionals, and retirees alike, offering
          a high quality of life with its excellent schools, parks, and
          accessibility to downtown Vancouver.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kerrisdale-vancouver:Things To Do Kerrisdale Paragraph">
          Kerrisdale offers a variety of activities and attractions catering to
          diverse interests. For nature enthusiasts, the neighborhood’s numerous
          parks provide serene environments for walking, jogging, or picnicking.
          <LinkText href="https://vancouver.ca/streets-transportation/arbutus-greenway.aspx">
            The Arbutus Greenway
          </LinkText>
          , a converted railway corridor, offers a scenic route for cyclists and
          pedestrians, connecting various parts of Vancouver. Art lovers can
          explore local galleries showcasing works by Canadian artists, while
          shoppers can enjoy the unique boutiques and specialty stores in{' '}
          <LinkText href="http://www.kerrisdalevillage.com/">
            Kerrisdale Village
          </LinkText>
          . Foodies will find a diverse culinary scene, with restaurants
          offering cuisines ranging from traditional Canadian to international
          flavors.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.kerrisdale-vancouver:Restaurants Kerrisdale Paragraph">
        <Paragraph>
          Kerrisdale Vancouver offers a range of dining options for every taste.
          Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://sushibarajisai.com/">
            Ajisai Sushi Bar
          </LinkText>{' '}
          - Ajisai Sushi Bar is renowned for its fresh and authentic Japanese
          cuisine. The restaurant offers a cozy and minimalist setting, allowing
          the quality of the food to take center stage. Specializing in sushi
          and sashimi, their menu features a wide selection of rolls, nigiri,
          and seasonal specialties. Patrons often praise the freshness of the
          fish and the artistry of the presentation. Ajisai is a favorite among
          sushi enthusiasts seeking an authentic experience.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://www.bufala.ca/">Bufala Kerrisdale</LinkText> -
          Bufala Kerrisdale is a vibrant Italian eatery known for its wood-fired
          pizzas and contemporary Italian dishes. The ambiance is casual yet
          chic, making it suitable for both family dinners and casual outings.
          Their menu features a variety of pizzas with creative toppings,
          alongside classic pasta dishes and hearty mains. Bufala’s commitment
          to using fresh, local ingredients shines through in each dish, making
          it a staple in the Kerrisdale dining scene.
        </Paragraph>
        <Paragraph>
          3) <LinkText href="http://sandeulkorean.ca/">Sandeul</LinkText> -
          Sandeul is a Korean restaurant in Kerrisdale, offering a variety of
          traditional Korean dishes. The restaurant provides a cozy and
          welcoming atmosphere, making it a great spot for both lunch and
          dinner. Diners can enjoy a range of Korean specialties, including
          bibimbap, bulgogi, and various kimchi dishes.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kerrisdale-vancouver:Transportation Kerrisdale Paragraph">
          Getting around Kerrisdale, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of Kerrisdale are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, Kerrisdale offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.kerrisdale-vancouver:Where To Stay Kerrisdale Paragraph">
          Accommodation options in Kerrisdale, Vancouver cater to those looking
          for a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          Kerrisdale itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in Kerrisdale
          allows visitors to experience its distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default Kerrisdale
